import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Loader2, Download } from "lucide-react";

/**
 * Recruitment power-triage — instant Resume / Portfolio preview in a side
 * panel, no download required. Fetches the file as a blob (through the
 * authenticated `api` client, same pattern as the rest of the app) and
 * renders it inline for PDFs/images; anything else falls back to a
 * "Download to view" action inside the panel.
 */
export function CandidateDocumentViewer({ open, path, title, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState("");

  useEffect(() => {
    if (!open || !path) return;
    let url = null;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) return;
        url = URL.createObjectURL(resp.data);
        setMimeType(resp.data.type || "");
        setBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, path]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = title || "document";
    a.click();
  };

  const isPdf = mimeType.includes("pdf");
  const isImage = mimeType.startsWith("image/");

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col gap-0" data-testid="doc-viewer-sheet">
        <SheetHeader className="p-4 border-b border-slate-200 text-left">
          <SheetTitle className="font-display truncate pr-6">{title}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center">
          {loading && <Loader2 className="animate-spin text-slate-400" size={28} data-testid="doc-viewer-loading" />}
          {!loading && error && (
            <div className="text-sm text-slate-500 p-6 text-center" data-testid="doc-viewer-error">Couldn&apos;t load this file.</div>
          )}
          {!loading && !error && blobUrl && isPdf && (
            <iframe src={blobUrl} title={title} className="w-full h-full border-0" data-testid="doc-viewer-pdf" />
          )}
          {!loading && !error && blobUrl && isImage && (
            <img src={blobUrl} alt={title} className="max-w-full max-h-full object-contain" data-testid="doc-viewer-image" />
          )}
          {!loading && !error && blobUrl && !isPdf && !isImage && (
            <div className="text-sm text-slate-500 p-6 text-center">
              This file type can&apos;t be previewed inline.
              <div className="mt-3">
                <Button size="sm" onClick={download} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="doc-viewer-download-btn">
                  <Download size={13} className="mr-1.5" />Download to view
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
